// Page: DemoTemplates
// Purpose: Public gallery page showing static and video templates with a
// masonry layout, lightweight preview modal, and CTA to sign up.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import { X, ExternalLink, Play } from 'lucide-react';
import useDarkMode from '@/hooks/useDarkMode';
import { getPublicTemplates } from '@/api/content/templatesApi';
import { getPublicVideoTemplates } from '@/api/content/videoTemplatesApi';
import { TemplateCardSkeleton, TemplateGridSkeleton } from '@/components/ui/feedback/skeleton.jsx';
import '@/components/templates/templates/MasonryLayout.css';
import SEOHead from '@/components/shared/SEOHead';

// Add all the animation keyframes in one place
const animations = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes pulseStaggered {
    0% { opacity: 0.6; }
    50% { opacity: 0.8; }
    100% { opacity: 0.6; }
  }
`;

// Fallback hero images in case API fails
const fallbackImages = Array.from({ length: 19 }, (_, i) => `/images/hero/hero${i + 1}.png`);

export default function DemoTemplates() {
  const isDark = useDarkMode();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [videoTemplates, setVideoTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templateType, setTemplateType] = useState('static'); // 'static' or 'video'
  const [hoveredVideoId, setHoveredVideoId] = useState(null);
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Format date for display from Firestore timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Handle Firestore timestamp format
    if (timestamp._seconds) {
      const date = new Date(timestamp._seconds * 1000);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }
    
    // Handle regular date string
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Get template image URL
  const getTemplateImageUrl = (template) => {
    // Use the template image URL if it exists
    if (template.imageUrl) return template.imageUrl;
    
    // Otherwise, construct the URL from the template ID
    return `https://refrral.online/api/templates/${template.id}/image`;
  };

  // Check if published date exists and is valid
  const hasValidPublishedDate = (template) => {
    if (!template || !template.datePublished) return false;
    
    // Handle Firestore timestamp format
    if (typeof template.datePublished === 'object' && template.datePublished._seconds) {
      return true;
    }
    
    // Handle string date format
    if (typeof template.datePublished === 'string' && template.datePublished !== 'N/A') {
      return true;
    }
    
    return false;
  };

  // Fetch templates and video templates from API
  useEffect(() => {
    // Prevent duplicate calls in React StrictMode (development)
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch both templates and video templates
        const [templatesResponse, videoTemplatesResponse] = await Promise.all([
          getPublicTemplates().catch(err => {
            console.error('Error fetching templates:', err);
            return null;
          }),
          getPublicVideoTemplates().catch(err => {
            console.error('Error fetching video templates:', err);
            return null;
          })
        ]);

        // Handle templates
        if (templatesResponse && templatesResponse.templates && templatesResponse.templates.length > 0) {
          setTemplates(templatesResponse.templates);
        } else {
          // Fallback to default images if no templates returned
          setTemplates(fallbackImages.map((src, index) => ({
            id: `fallback-${index}`,
            imageUrl: src,
            company: 'Advocacy Lab',
            createdDate: new Date().toISOString(),
            datePublished: new Date().toISOString()
          })));
        }

        // Handle video templates
        if (videoTemplatesResponse && videoTemplatesResponse.videoTemplates && videoTemplatesResponse.videoTemplates.length > 0) {
          setVideoTemplates(videoTemplatesResponse.videoTemplates);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load templates');
        // Fallback to default images on error
        setTemplates(fallbackImages.map((src, index) => ({
          id: `fallback-${index}`,
          imageUrl: src,
          company: 'Advocacy Lab',
          createdDate: new Date().toISOString(),
          datePublished: new Date().toISOString()
        })));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleTheme = () => {
    // Use the same pattern as LandingHeader - check the hook value directly
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  const openModal = (template) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedTemplate(null);
    setIsModalOpen(false);
  };

  const navigateToSignup = () => {
    navigate('/signup');
  };

  const ToggleButton = () => {
    return (
      <div className="flex items-center justify-center">
        <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 shadow-sm">
          <button
            onClick={() => setTemplateType('static')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              templateType === 'static'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400'
            }`}
          >
            Static Templates
          </button>
          <button
            onClick={() => setTemplateType('video')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
              templateType === 'video'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400'
            }`}
          >
            Video Templates
          </button>
        </div>
      </div>
    );
  };

    // Video template card with hover functionality (matching VideoTemplateGrid)
  const VideoTemplateCard = ({ videoTemplate }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const [thumbnailError, setThumbnailError] = useState(false);
    const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isImagePreloaded, setIsImagePreloaded] = useState(false);
    const videoRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    // Convert Firebase Storage URLs
    const convertFirebaseStorageUrl = (gsUrl) => {
      if (!gsUrl) return gsUrl;
      
      // If it's already an HTTP URL (signed URL from backend), return as-is
      if (gsUrl.startsWith('http://') || gsUrl.startsWith('https://')) {
        return gsUrl;
      }
      
      // Only convert gs:// URLs (for backward compatibility)
      if (!gsUrl.startsWith('gs://')) {
        return gsUrl; // Return as-is if not a gs:// URL
      }
      
      // Extract bucket and path from gs://bucket/path
      const match = gsUrl.match(/^gs:\/\/([^\/]+)\/(.+)$/);
      if (!match) {
        return gsUrl; // Return as-is if format doesn't match
      }
      
      const [, bucket, path] = match;
      // Convert to Firebase Storage HTTP URL
      return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
    };

    const convertedVideoUrl = convertFirebaseStorageUrl(videoTemplate.videoUrl);

    // Preload video for immediate playback
    useEffect(() => {
      if (videoRef.current && convertedVideoUrl) {
        // Load video metadata and buffer for immediate playback
        videoRef.current.load();
        // Prepare video for faster playback by starting to buffer
        const prepareVideo = async () => {
          try {
            if (videoRef.current) {
              // Set to start of video and prepare for immediate play
              videoRef.current.currentTime = 0;
              // Don't actually play yet, just prepare
            }
          } catch (error) {
            // Ignore errors during preparation
          }
        };
        prepareVideo();
      }
    }, [convertedVideoUrl]);

    // Generate thumbnail from video if no thumbnail provided
    useEffect(() => {
      if (!videoTemplate.thumbnailUrl && convertedVideoUrl && !generatedThumbnail && !thumbnailError) {
        generateThumbnailFromVideo();
      }
    }, [videoTemplate.thumbnailUrl, convertedVideoUrl, generatedThumbnail, thumbnailError]);

    // Preload thumbnail image
    useEffect(() => {
      const thumbnailUrl = videoTemplate.thumbnailUrl || generatedThumbnail;
      if (thumbnailUrl) {
        const img = new Image();
        img.src = thumbnailUrl;
        img.onload = () => {
          setIsImagePreloaded(true);
          setImageLoaded(true);
        };
        img.onerror = () => {
          setThumbnailError(true);
          setIsImagePreloaded(true);
          setImageLoaded(true);
        };
      }
    }, [videoTemplate.thumbnailUrl, generatedThumbnail]);

    const generateThumbnailFromVideo = async () => {
      try {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.playsInline = true;
        
        const thumbnailPromise = new Promise((resolve, reject) => {
          video.onloadeddata = () => {
            try {
              const seekTime = Math.min(video.duration * 0.25, 0.5);
              video.currentTime = seekTime;
            } catch (err) {
              reject(new Error('Failed to seek video'));
            }
          };
          
          video.onseeked = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              
              const ctx = canvas.getContext('2d');
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              
              const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
              resolve(thumbnailDataUrl);
              
              video.pause();
              if (video.src.startsWith('blob:')) {
                URL.revokeObjectURL(video.src);
              }
            } catch (err) {
              reject(new Error('Failed to generate thumbnail'));
            }
          };
          
          video.onerror = () => {
            reject(new Error('Failed to load video'));
            if (video.src.startsWith('blob:')) {
              URL.revokeObjectURL(video.src);
            }
          };
        });

        video.src = convertedVideoUrl;
        video.load();

        const thumbnailUrl = await thumbnailPromise;
        setGeneratedThumbnail(thumbnailUrl);
      } catch (error) {
        console.error('Error generating thumbnail:', error);
        setThumbnailError(true);
      }
    };

    const handleMouseEnter = () => {
      setIsHovered(true);
      
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      
      // Start playing video immediately (no delay)
      if (videoRef.current && convertedVideoUrl) {
        videoRef.current.currentTime = 0;
        const playPromise = videoRef.current.play();
        if (playPromise) {
          playPromise.catch((error) => {
            console.log('Video play failed:', error);
            setVideoError(true);
          });
        }
      }
  };

  const handleMouseLeave = () => {
      setIsHovered(false);
      
      // Clear hover timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      
      // Pause video
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    };

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    const thumbnailSrc = videoTemplate.thumbnailUrl || generatedThumbnail;

    return (
      <div 
        className="masonry-item cursor-pointer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => openModal(videoTemplate)}
      >
        <div className="masonry-item-inner bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md dark:hover:shadow-gray-700 transition-all duration-200">
          <div className="relative overflow-hidden">
            {/* Skeleton loader while image is loading */}
            {!imageLoaded && (
              <div className="bg-gray-200 dark:bg-gray-700 animate-pulse w-full aspect-video"></div>
            )}
            
            {/* Video - show when hovered */}
            {convertedVideoUrl && (
              <video
                ref={videoRef}
                className={`absolute inset-0 w-full h-full object-cover z-10 ${
                  isHovered && imageLoaded ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                style={{ transition: 'opacity 0.2s ease-in-out' }}
                crossOrigin="anonymous"
                muted
                playsInline
                loop
                preload="auto"
                onError={() => setVideoError(true)}
              >
                <source src={convertedVideoUrl} type="video/mp4" />
              </video>
            )}
            
            {/* Thumbnail */}
            {!thumbnailError && thumbnailSrc ? (
              <div 
                className={`relative ${imageLoaded ? 'opacity-100' : 'opacity-0'} ${
                  isHovered && convertedVideoUrl && !videoError ? 'opacity-0' : 'opacity-100'
                }`} 
                style={{ 
                  transition: 'opacity 0.2s ease-in-out',
                  visibility: imageLoaded ? 'visible' : 'hidden' 
                }}
              >
                <img
                  src={thumbnailSrc}
                  alt={videoTemplate.title || 'Video Template'}
                  className="w-full h-auto display-block"
                  onLoad={() => setImageLoaded(true)}
                  onError={() => {
                    setThumbnailError(true);
                    setImageLoaded(true);
                  }}
                />
              </div>
            ) : (
              <div className="bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-center p-4 aspect-video">
                <Play className="w-8 h-8 text-gray-400" />
              </div>
            )}
            
            {/* Overlay gradient that appears on hover */}
            {isHovered && imageLoaded && (
              <div 
                className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10 z-20"
                style={{ animation: 'fadeIn 0.2s ease-out' }}
              >
                {/* Video template title on hover */}
                <div className="absolute bottom-0 left-0 right-0 px-4 py-3 z-30">
                  <p className="text-white text-sm font-medium truncate">
                    {videoTemplate.title || videoTemplate.company || 'Video Template'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Static template card
  const StaticTemplateCard = ({ template }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    return (
      <div 
        className="masonry-item cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => openModal(template)}
      >
        <div className="masonry-item-inner relative bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden">
          <img
            src={getTemplateImageUrl(template)}
            alt={template.company || 'Template'}
            className={`w-full h-auto object-cover transition-transform duration-300 ${
              isHovered ? 'scale-105' : 'scale-100'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          
          {/* Company name overlay on hover */}
          {isHovered && imageLoaded && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
              <p className="text-white text-sm font-medium">
                {template.company || 'Advocacy Lab'}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Masonry layout component
  const MasonryGrid = ({ items, isVideo = false }) => {
    const [columns, setColumns] = useState(4);
    const [columnItems, setColumnItems] = useState([]);

    // Update columns based on screen size
    useEffect(() => {
      const updateColumns = () => {
        const width = window.innerWidth;
        if (width < 640) {
          setColumns(2);
        } else if (width < 1024) {
          setColumns(3);
        } else {
          setColumns(isVideo ? 3 : 4);
        }
      };

      updateColumns();
      window.addEventListener('resize', updateColumns);
      return () => window.removeEventListener('resize', updateColumns);
    }, [isVideo]);

    // Distribute items across columns
    useEffect(() => {
      const distributed = Array(columns).fill().map(() => []);
      items.forEach((item, index) => {
        distributed[index % columns].push(item);
      });
      setColumnItems(distributed);
    }, [items, columns]);

    return (
      <div className="masonry-wrapper">
        <div className="masonry-grid">
          {columnItems.map((columnItems, columnIndex) => (
            <div key={columnIndex} className="masonry-column" style={{ width: `${100 / columns}%` }}>
              {columnItems.map((item) => (
                isVideo ? (
                  <VideoTemplateCard key={item.id} videoTemplate={item} />
                ) : (
                  <StaticTemplateCard key={item.id} template={item} />
                )
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Skeleton loader for masonry
  const MasonrySkeleton = ({ isVideo = false }) => {
    const [columns, setColumns] = useState(4);

    useEffect(() => {
      const updateColumns = () => {
        const width = window.innerWidth;
        if (width < 640) {
          setColumns(2);
        } else if (width < 1024) {
          setColumns(3);
        } else {
          setColumns(isVideo ? 3 : 4);
        }
      };

      updateColumns();
      window.addEventListener('resize', updateColumns);
      return () => window.removeEventListener('resize', updateColumns);
    }, [isVideo]);

    return (
      <div className="masonry-wrapper">
        <div className="masonry-grid">
          {Array(columns).fill().map((_, columnIndex) => (
            <div key={columnIndex} className="masonry-column" style={{ width: `${100 / columns}%` }}>
              {Array(Math.ceil(10 / columns)).fill().map((_, itemIndex) => (
                <div key={itemIndex} className="masonry-item mb-4">
                  <div className="masonry-item-inner bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse">
                    <div className={`w-full ${isVideo ? 'aspect-video' : 'aspect-square'} bg-gradient-to-b from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-lg`}></div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
      <SEOHead pageType="templates-landing" />
      {/* Inject animations */}
      <style dangerouslySetInnerHTML={{ __html: animations }} />
      
      {/* Use the SimpleLandingHeader component */}
      <SimpleLandingHeader 
        isDark={isDark} 
        toggleTheme={toggleTheme} 
        backLink="/"
        showLogo={true}
      />
      
      {/* Templates Hero Section */}
      <section className="bg-white dark:bg-gray-900 py-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl md:text-center xl:max-w-none mb-4">
            <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400 text-center">Templates</h2>
            <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight mx-auto text-center max-w-[90%]">
              {templateType === 'video' ? 'Video Templates' : '500+ High-Converting Templates'}
            </p>
            <p className="mt-6 text-lg text-gray-600 dark:text-gray-300 mx-auto text-center max-w-[90%]">
              {templateType === 'video' 
                ? 'Browse our collection of professionally designed video templates for social media and marketing campaigns.'
                : 'Browse our collection of 500+ professionally designed templates to jumpstart your marketing campaigns.'
              }
            </p>
            
            {/* Toggle Button */}
            <div className="mt-8">
              <ToggleButton />
            </div>
            
            {/* Error message */}
            {error && (
              <div className="mt-6 max-w-md mx-auto">
                <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-700/20 rounded-lg px-8 py-6 text-center border border-gray-200 dark:border-gray-700">
                  <svg className="w-10 h-10 text-gray-400 dark:text-gray-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Unable to load templates</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    We're having trouble loading the templates. Please check back later.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Templates Grid */}
      <section className="bg-white dark:bg-gray-900 pt-0 pb-20 px-4">
        <div className="mx-auto max-w-7xl">
          {isLoading ? (
            <MasonrySkeleton isVideo={templateType === 'video'} />
          ) : (
            <>
              <MasonryGrid 
                items={templateType === 'video' ? videoTemplates : templates} 
                isVideo={templateType === 'video'} 
                      />
                      
              {/* Disclaimer */}
              {((templateType === 'static' && templates.length > 0) || (templateType === 'video' && videoTemplates.length > 0)) && (
                <div className="mx-auto mt-12 mb-8 max-w-4xl px-6 text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Templates based on publicly available designs used in real political campaigns and advertisements. The inclusion of these designs does not imply any affiliation, partnership, or connection with the original campaign, candidate, or organization. It does not constitute an endorsement of our services by them, nor does it grant any right or license to use their original branding, images, or other copyrighted materials, which remain the property of their respective owners.
                  </p>
                </div>
              )}
                      
              {/* Sign-up button */}
              {((templateType === 'static' && templates.length > 0) || (templateType === 'video' && videoTemplates.length > 0)) && (
                <div className="mt-16 text-center">
                  <button
                    onClick={() => navigate('/signup')}
                    className="inline-flex items-center justify-center px-8 py-3.5 rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    {templateType === 'video' ? 'Sign up to access all video templates' : 'Sign up to access all 500+ templates'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Template Modal */}
      {isModalOpen && selectedTemplate && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 md:p-4 bg-black/50 backdrop-blur-sm">
          <div className="animate-in fade-in zoom-in-95 duration-200 ease-out w-[95%] md:w-full mx-auto">
            <div className="relative bg-white dark:bg-gray-900 rounded-xl md:rounded-2xl shadow-xl w-full max-w-3xl md:max-w-4xl lg:max-w-5xl max-h-[80vh] md:max-h-[90vh] overflow-hidden mx-auto my-1 md:my-8">
              {/* Close button */}
              <button 
                onClick={closeModal}
                className="absolute top-2 right-2 md:top-4 md:right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 transition-colors z-20 shadow-md"
                aria-label="Close"
              >
                <X className="w-4 h-4 md:w-5 md:h-5 text-gray-700 dark:text-gray-300" />
              </button>

              {/* Content with side-by-side layout */}
              <div className="flex flex-col lg:flex-row h-full">
                {/* Left side - Image or Video */}
                <div className="lg:w-1/2 flex-shrink-0 flex items-center justify-center p-6 md:p-8 lg:p-12 relative">
                  {/* For video templates, show video player */}
                  {templateType === 'video' && selectedTemplate.videoUrl ? (
                    <div className="relative w-full">
                      <video
                        src={selectedTemplate.videoUrl.startsWith('gs://') ? 
                          selectedTemplate.videoUrl.replace('gs://', 'https://firebasestorage.googleapis.com/v0/b/').replace('/', '/o/').replace(/\/(.*)$/, '/$1?alt=media') : 
                          selectedTemplate.videoUrl
                        }
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-auto max-h-[35vh] md:max-h-[50vh] lg:max-h-[65vh] object-contain rounded-lg shadow-lg"
                        style={{ aspectRatio: '16/9' }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ) : (
                    /* For static templates, show image */
                  <img 
                    src={getTemplateImageUrl(selectedTemplate)} 
                    alt="Template preview"
                    className="max-w-full h-auto max-h-[35vh] md:max-h-[50vh] lg:max-h-[65vh] object-contain rounded-lg shadow-lg"
                    style={{ width: 'auto' }}
                  />
                  )}
                </div>

                {/* Right side - Template details */}
                <div className="lg:w-1/2 flex-1 overflow-auto border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 max-h-[50vh] lg:max-h-none">
                  <div className="p-4 md:p-4 lg:p-10">
                    <div className="flex flex-col">
                      <div className="space-y-4 md:space-y-6 flex-grow text-sm md:text-base">
                        {/* Blurred Info Items */}
                        <div className="space-y-4 md:space-y-6 mb-4 md:mb-6 filter blur-[2px] pointer-events-none">
                          {/* Title field for video templates */}
                          {templateType === 'video' && selectedTemplate.title && (
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                              <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Title:</div>
                              <div className="flex-1 text-gray-800 dark:text-white break-words font-medium">
                                {selectedTemplate.title}
                              </div>
                            </div>
                          )}

                          {/* Creator field */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                            <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Creator:</div>
                            <div className="flex-1 text-gray-800 dark:text-white break-words">
                              {selectedTemplate.company || 'Advocacy Lab'}
                            </div>
                          </div>

                          {/* Published Date */}
                          {hasValidPublishedDate(selectedTemplate) && (
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                              <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Published:</div>
                              <div className="flex-1 flex items-center text-gray-800 dark:text-white">
                                <span>{formatDate(selectedTemplate.datePublished)}</span>
                              </div>
                            </div>
                          )}

                          {/* Added Date */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                            <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Added:</div>
                            <div className="flex-1 flex items-center text-gray-800 dark:text-white">
                              <span>{formatDate(selectedTemplate.createdDate || selectedTemplate.createdAt)}</span>
                            </div>
                          </div>

                          {/* Video Duration - only show for video templates */}
                          {templateType === 'video' && selectedTemplate.duration && (
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                              <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Duration:</div>
                              <div className="flex-1 flex items-center text-gray-800 dark:text-white">
                                <span>{selectedTemplate.duration}s</span>
                              </div>
                            </div>
                          )}

                          {/* Landing Page Link - only show for static templates */}
                          {templateType !== 'video' && (
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 md:gap-1">
                            <div className="w-full sm:w-[180px] text-gray-500 dark:text-gray-300 flex-shrink-0 font-medium mb-1 sm:mb-0">Landing Page:</div>
                            <div className="flex-1 mt-1 sm:mt-0">
                              <span className="flex items-center justify-center w-full px-2 md:px-3 py-1 md:py-1.5 text-xs md:text-sm border border-gray-300 dark:border-gray-500 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-100">
                                <span>View</span>
                                <ExternalLink className="h-2.5 w-2.5 md:h-3 md:w-3 ml-1 md:ml-1.5" />
                              </span>
                            </div>
                          </div>
                          )}
                        </div>

                        {/* Ad Copy Section - Blurred - only show for static templates */}
                        {templateType !== 'video' && (
                        <div className="space-y-2 md:space-y-2 filter blur-[2px] pointer-events-none">
                          <div className="flex items-center justify-between">
                            <div className="text-gray-500 dark:text-gray-300 font-medium">Ad Copy:</div>
                          </div>
                          <div className="w-full p-3 md:p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 max-h-[80px] md:max-h-[120px] overflow-y-auto">
                            <p className="whitespace-pre-line text-gray-700 dark:text-gray-100 text-xs md:text-sm">
                              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.
                            </p>
                          </div>
                        </div>
                        )}

                        {/* Account required notice */}
                        <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800/30">
                          <p className="text-sm text-purple-800 dark:text-purple-300">
                            Sign up for an account to access full template details and editing capabilities.
                          </p>
                        </div>
                      </div>
                      
                      {/* Footer button */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 md:pt-6 mt-4 md:mt-6">
                        <div className="flex flex-col space-y-3 pb-5 sm:pb-4 md:pb-0">
                          <button
                            onClick={navigateToSignup}
                            className="w-full py-2.5 md:py-3 px-3 md:px-4 rounded-md text-sm md:text-base font-medium text-white transition-colors flex items-center justify-center hover:shadow-lg"
                            style={{
                              background: "linear-gradient(45deg, #00C4CC, #7D2AE8)",
                              boxShadow: "0 2px 6px rgba(0, 196, 204, 0.3)",
                              transition: "box-shadow 0.3s ease, filter 0.3s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "linear-gradient(45deg, #00D4DC, #8D3AF8)";
                              e.currentTarget.style.filter = "brightness(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "linear-gradient(45deg, #00C4CC, #7D2AE8)";
                              e.currentTarget.style.filter = "none";
                            }}
                          >
                            Sign Up to Edit in Canva
                          </button>
                          
                          {/* Close button - only visible on mobile */}
                          <button
                            onClick={closeModal}
                            className="w-full py-2.5 px-3 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 transition-colors lg:hidden mb-2 sm:mb-3 md:mb-4"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 